"use client";

import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Bottom Sheet primitive.
 * - 모바일에서 modal/popup을 대체하는 하단 시트
 * - @base-ui/react Dialog 기반: focus trap, scroll lock, ESC dismiss 자동 처리
 * - safe-area-inset-bottom 자동 패딩
 *
 * 사용 예:
 *   <Sheet open={open} onOpenChange={setOpen} title="주문" description="...">
 *     <SheetContent>...</SheetContent>
 *   </Sheet>
 */

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** 헤더 타이틀 (optional, 없으면 헤더 미표시) */
  title?: React.ReactNode;
  /** 헤더 설명 (a11y용, 화면에는 sr-only) */
  description?: string;
  /** 시트 컨텐츠 max-height (기본 85svh) */
  maxHeight?: string;
  /** 추가 className (시트 popup용) */
  className?: string;
  /** overlay tap 시 close 비활성화 (기본 false=dismissible) */
  disableOutsideDismiss?: boolean;
}

export function Sheet({
  open,
  onOpenChange,
  children,
  title,
  description,
  maxHeight = "85svh",
  className,
  disableOutsideDismiss = false,
}: SheetProps) {
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(o) => onOpenChange(o)}
      disablePointerDismissal={disableOutsideDismiss}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm",
            "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
            "transition-opacity duration-200"
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            "fixed bottom-0 left-0 right-0 z-[70]",
            "flex flex-col rounded-t-2xl border-t border-black/[0.08]",
            "bg-[#ffffff] text-neutral-900 outline-none",
            "pb-[max(8px,env(safe-area-inset-bottom))]",
            "data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full",
            "transition-transform duration-300 ease-out",
            className
          )}
          style={{ maxHeight }}
        >
          {/* Drag handle (시각용; 실제 swipe 닫기는 backdrop tap으로 대체) */}
          <div className="flex justify-center pt-2 pb-1 shrink-0">
            <div className="h-1 w-10 rounded-full bg-black/15" />
          </div>

          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-4 pt-1 pb-3 shrink-0">
              <DialogPrimitive.Title className="text-base font-semibold tracking-[-0.224px] text-neutral-900 min-w-0 truncate">
                {title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close
                className="p-1.5 -mr-1.5 rounded-md text-black/65 hover:text-black/80 active:bg-black/[0.06] shrink-0"
                aria-label="닫기"
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>
          )}
          {description && (
            <DialogPrimitive.Description className="sr-only">
              {description}
            </DialogPrimitive.Description>
          )}

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {children}
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** 시트 컨텐츠 영역 wrapper (기본 패딩 적용) */
export function SheetContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("px-4 pb-4", className)}>{children}</div>;
}
